import React, { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { MessageAttachments } from '@/components/common/MessageAttachments';
import { InlineMediaText } from '@/components/common/InlineStepMedia';
import { hasInlineMarker, injectInlineMountPoints, prepareInlineMedia } from '@/utils/inlineMedia';
import { useT } from '@/i18n/labels';
import { renderMarkdown } from '@/utils/markdown';
import { sanitizeHtml } from '@/utils/sanitize';
import { useLangStore } from '@/stores/langStore';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { he as heDateLocale } from 'date-fns/locale';
import { formatDraftAge, isDraftHeld } from '@/utils/draftReview';
import type { PendingEmailDraft } from '@/types';

export type DraftAction = 'approve' | 'revise' | 'edit';

export interface DraftReviewCardProps {
  draft: PendingEmailDraft;
  /** This console's own agent id (auth store `user.agentId`), used with draft.assignedToOther. */
  myAgentId?: string | null;
  /** Emitted on Approve / Revise-submit / Edit-submit. `text` is undefined for approve. */
  onAct: (action: DraftAction, text?: string) => void | Promise<void>;
}

type PanelMode = null | 'revise' | 'edit';

/**
 * ADR-019 D5/D6/D7 — three-button review card for a bot-drafted Email reply
 * gated for human review before it reaches the customer. Pure presentational:
 * does not call the hub, does not read the conversation store, and does not
 * emit a 'view' action (a parallel task owns the view-tracking effect).
 *
 * OP-582 — the card must never claim a send that cannot happen. When
 * `draft.autoSendEnabled === false` (tenant auto-send off, or a Consultation Only
 * channel) NO clock can release the draft, yet `maxWaitAtUtc` is snapshotted and
 * passes anyway, so the card used to read "Sending now…" permanently for a reply
 * only a person could send. It now says it is waiting for a person, and shows the
 * draft's AGE — the number that tells an agent this is being neglected.
 */
export const DraftReviewCard: React.FC<DraftReviewCardProps> = ({ draft, myAgentId, onAct }) => {
  const { t } = useT();
  const lang = useLangStore((s) => s.lang);

  const [mode, setMode] = useState<PanelMode>(null);
  const [reviseText, setReviseText] = useState('');
  const [editText, setEditText] = useState(draft.draftText);
  const [submitting, setSubmitting] = useState(false);
  const [, setTick] = useState(0);

  // 2026-10-12 - a bot draft may carry [[media:K]] picture markers (K = position in draft.attachments). Markers that point at
  // nothing are removed here exactly as the email renderer removes them, so the reviewer never reads a raw token.
  const inlineView = hasInlineMarker(draft.draftText)
    ? prepareInlineMedia(draft.draftText, draft.attachments)
    : { text: draft.draftText, placed: [] as number[] };
  const remainingAttachments = (draft.attachments ?? []).filter((_a, i) => !inlineView.placed.includes(i));

  // Reset on new draft/revision — a re-draft must never leave the agent
  // staring at stale text they might send.
  useEffect(() => {
    setMode(null);
    setReviseText('');
    setEditText(draft.draftText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [`${draft.draftId}:${draft.revision}`]);

  // Live 30s countdown re-render; cleared on unmount.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const runAction = async (action: DraftAction, text?: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onAct(action, text);
    } catch {
      // The parent surfaces errors; the card must not throw.
    } finally {
      setSubmitting(false);
    }
  };

  const submitRevise = () => {
    const text = reviseText.trim();
    if (!text) return;
    void runAction('revise', text);
  };

  const submitEdit = () => {
    const text = editText.trim();
    if (!text) return;
    void runAction('edit', text);
  };

  // `assignedToOther === true` with `assignedAgentId === myAgentId` means "I am the owner
  // and this is merely the awareness copy" ⇒ the card stays fully actionable — read-only
  // only applies when the draft belongs to a DIFFERENT agent.
  const readOnly = draft.assignedToOther === true && draft.assignedAgentId !== myAgentId;

  const dateLocale = lang === 'he' ? heDateLocale : undefined;

  // OP-582 — the hold test is `=== false`, never a falsy check. `undefined` means the server
  // did not send the field (a build that predates it), and inventing a hold the server never
  // declared would be the same class of lie in the opposite direction.
  const held = isDraftHeld(draft);

  const now = Date.now();
  const deadline = parseISO(draft.maxWaitAtUtc);
  const isDue = deadline.getTime() <= now;

  // A held draft still carries a snapshotted maxWaitAtUtc that passes like any other, so both
  // countdown strings were false for it — "Sends automatically in …" before the deadline and
  // "Sending now…" permanently after. Neither ever came true: only a person can release it.
  const countdownText = held
    ? t('draft_hold_no_autosend')
    : isDue
    ? t('draft_autosend_due')
    : t('draft_autosend_in', {
        time: formatDistanceToNowStrict(deadline, { locale: dateLocale }),
      });

  // AGE, not deadline — see formatDraftAge. On a held draft there is no deadline to read, so
  // this is the only number that says a customer is being left waiting.
  const age = formatDraftAge(draft.createdAtUtc, lang);
  const ageText = age === null ? null : t('draft_waiting_for', { time: age });

  return (
    <div className="ac-consult-req ac-draft-card" data-testid="email-draft-card">
      <div className="ac-consult-req-head">
        <Icon name="mail" size={14} />
        <span>{t('draft_title')}</span>
        <span className="ac-badge" data-testid="email-draft-revision">
          {t('draft_revision', { n: draft.revision })}
        </span>
      </div>

      <span data-testid="email-draft-countdown" data-held={held ? 'true' : 'false'}>
        {countdownText}
      </span>

      {ageText && <span data-testid="email-draft-age">{ageText}</span>}

      {draft.revisionFailureReason && (
        <div role="alert" data-testid="email-draft-revise-failed">
          {t('draft_revise_failed', { reason: draft.revisionFailureReason })}
        </div>
      )}

      {draft.actionRejected && (
        <div role="alert" data-testid="email-draft-rejected">
          {t('draft_rejected_not_owner')}
        </div>
      )}

      {inlineView.placed.length > 0 ? (
        // 2026-10-12 - the draft carries [[media:K]] markers: show each picture beside the step the customer will see it under.
        <InlineMediaText
          className="ac-consult-req-text"
          testId="email-draft-text"
          html={injectInlineMountPoints(sanitizeHtml(renderMarkdown(inlineView.text)))}
          attachments={draft.attachments ?? []}
        />
      ) : (
        <div className="ac-consult-req-text" data-testid="email-draft-text" dir="auto"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMarkdown(inlineView.text)) }}
        />
      )}
      {/* 2026-09-05 - the reviewer sees exactly what the customer will receive: the bot's bank images /
          files ride the draft (ADR-019) and are sent with the approved text. Pictures placed inline above
          leave this row; any that were not placed still show here. */}
      {remainingAttachments.length > 0 && (
        <div data-testid="email-draft-attachments">
          <MessageAttachments attachments={remainingAttachments} />
        </div>
      )}

      {readOnly ? (
        <span data-testid="email-draft-readonly">{t('draft_readonly_hint')}</span>
      ) : (
        <>
          <div>{t('draft_review_hint')}</div>
          <div>
            <button
              type="button"
              className="ac-btn primary sm"
              data-testid="email-draft-approve"
              disabled={submitting}
              onClick={() => void runAction('approve')}
            >
              {t('draft_btn_approve')}
            </button>
            <button
              type="button"
              className="ac-btn ghost sm"
              data-testid="email-draft-revise"
              disabled={submitting}
              onClick={() => setMode(mode === 'revise' ? null : 'revise')}
            >
              {t('draft_btn_revise')}
            </button>
            <button
              type="button"
              className="ac-btn ghost sm"
              data-testid="email-draft-edit"
              disabled={submitting}
              onClick={() => setMode(mode === 'edit' ? null : 'edit')}
            >
              {t('draft_btn_edit')}
            </button>
          </div>

          {mode === 'revise' && (
            <div>
              <textarea
                dir="auto"
                data-testid="email-draft-revise-input"
                placeholder={t('draft_revise_placeholder')}
                value={reviseText}
                disabled={submitting}
                onChange={(e) => setReviseText(e.target.value)}
              />
              <button
                type="button"
                className="ac-btn primary sm"
                data-testid="email-draft-revise-submit"
                disabled={submitting || !reviseText.trim()}
                onClick={submitRevise}
              >
                {t('draft_revise_send')}
              </button>
              <button
                type="button"
                className="ac-btn ghost sm"
                data-testid="email-draft-cancel"
                disabled={submitting}
                onClick={() => setMode(null)}
              >
                {t('set_cancel')}
              </button>
            </div>
          )}

          {mode === 'edit' && (
            <div>
              <textarea
                dir="auto"
                data-testid="email-draft-edit-input"
                placeholder={t('draft_edit_placeholder')}
                value={editText}
                disabled={submitting}
                onChange={(e) => setEditText(e.target.value)}
              />
              {hasInlineMarker(editText) && (
                <div data-testid="email-draft-marker-hint">{t('draft_marker_hint')}</div>
              )}
              <button
                type="button"
                className="ac-btn primary sm"
                data-testid="email-draft-edit-submit"
                disabled={submitting || !editText.trim()}
                onClick={submitEdit}
              >
                {t('draft_edit_send')}
              </button>
              <button
                type="button"
                className="ac-btn ghost sm"
                data-testid="email-draft-cancel"
                disabled={submitting}
                onClick={() => setMode(null)}
              >
                {t('set_cancel')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
